// src/interfaces/user/QuotesList.js
import React, { useState, useEffect } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import BreakdownDisplay from '../../components/common/BreakdownDisplay';
import FileUpload from './FileUpload';
import Modal from '../../components/common/Modal.js';
import PrintroomReadyButton from './PrintroomReadyButton';

const getLaminateTypeName = (laminateTypeId) => {
  switch (laminateTypeId) {
    case 'matt_lam':
      return 'Matt';
    case 'gloss_lam':
      return 'Gloss';
    case 'soft_touch_lam':
      return 'Soft Touch';
    default:
      return '';
  }
};

const QuotesList = ({ setActivePage }) => { // Accept setActivePage as a prop
  const {
    quotes,
    isLoading,
    error,
    quotesTotal,
    quotesHasMore,
    fetchQuotesWithContext,
    loadMoreQuotes,
    approveAdjustedQuote,
    deleteQuote,
    prepareQuoteForEditing, // NEW: Get this from context
    pendingUploadQuoteId,
    clearPendingUploadRequest
  } = useQuotes();

  const { isAuthenticated, userRole, hasRole } = useAuth();

  // Determine the page title based on user role
  const isManagerOrAdmin = hasRole('manager') || hasRole('admin');
  const pageTitle = isManagerOrAdmin ? 'My Quotes' : 'My Orders';

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTargetQuote, setUploadTargetQuote] = useState(null);
  const [uploadStatusMessage, setUploadStatusMessage] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchQuotesWithContext('management', { search: searchTerm, limit: 50, offset: 0 });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, fetchQuotesWithContext]);

  // --- NEW: Edit Similar Handler ---
  const handleEditSimilar = async (quote) => {
    // prepareQuoteForEditing (from QuoteContext) should:
    // 1. Process the quote data (deep copy, prefix name "Copy of...", clear ID, etc.).
    // 2. Set `quoteToLoad` in context with the processed data.
    // 3. Set `targetCalculator` in context to 'small-format' or 'large-format'.
    await prepareQuoteForEditing(quote); // This function updates the context.

    if (typeof setActivePage === 'function') {
      setActivePage('calculator'); // Navigate to the calculator page/view
    } else {
      console.warn('QuotesList: setActivePage prop is not a function. Cannot navigate.');
      // Fallback alert if navigation fails, though ideally it shouldn't.
      // alert('Quote data prepared. Please manually navigate to the calculator.');
    }
  };

  // --- Existing functions (formatDate, getStatusBadge, etc.) ---
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    try {
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'adjusted': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-purple-100 text-purple-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getQuoteType = (quote) => {
    const firstItem = quote?.items?.[0];
    if (!firstItem) {
      if (quote?.material) return 'Large Format';
      if (quote?.paperStock) return 'Small Format';
      return 'Unknown';
    }
    if (firstItem.productType === 'small-format' || firstItem.productType === 'small_format' || firstItem.paperStock) {
      return 'Small Format';
    }
    if (firstItem.productType === 'large-format' || firstItem.productType === 'large_format' || firstItem.material) {
      return 'Large Format';
    }
    return 'Unknown';
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'Small Format': return 'bg-blue-100 text-blue-800';
      case 'Large Format': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const viewQuoteDetails = (quote) => {
    setSelectedQuote(quote);
  };

  const closeQuoteDetails = () => {
    setSelectedQuote(null);
  };

  const handleApproveAdjusted = (quoteId) => {
    approveAdjustedQuote(quoteId);
    setSelectedQuote(null);
  };

  const handleDeleteQuote = async (quoteId) => {
    const result = await deleteQuote(quoteId);
    if (result && result.success) {
      setSelectedQuote(null); // Close modal if open
    }
  };

  const getQuoteName = (quote) => {
    const firstItem = quote?.items?.[0];
    return quote.quoteName || firstItem?.jobName || quote.jobName || 'Untitled Quote';
  };

  const getQuoteSummary = (quote) => {
    const quoteType = getQuoteType(quote);
    const firstItem = quote?.items?.[0];

    if (quote.items && quote.items.length > 1) {
      return `${quote.items.length} items`;
    }
    if (firstItem) {
      if (quoteType === 'Small Format') {
        const stockDisplay = firstItem.paperStockName || firstItem.paperStock || 'N/A';
        return stockDisplay;
      }
      if (quoteType === 'Large Format') {
        if (firstItem && firstItem.jobType === 'Boards') {
          const boardMaterial = firstItem.materials?.find(m => m.type === 'board');
          if (boardMaterial?.name) {
            return boardMaterial.name;
          }
          let materialDisplayString = firstItem.materialNames || firstItem.material || '';
          const parts = materialDisplayString.split(',')
            .map(part => part.trim())
            .filter(part => part.toLowerCase() !== 'standard vinyl');

          const cleanedDisplayString = parts.join(', ');

          return cleanedDisplayString || 'N/A';
        } else {
          const materialDisplay = firstItem.materialNames || firstItem.material || 'N/A';
          return materialDisplay;
        }
      }
    }
    return quote.material || quote.materialNames || 'Details unavailable';
  };

  const getQuoteDimensions = (quote) => {
    const firstItem = quote?.items?.[0];
    if (quote.items && quote.items.length > 1) {
      return '';
    }
    if (firstItem) {
      return firstItem.dimensions || (firstItem.width && firstItem.height ? `${firstItem.width}x${firstItem.height}mm` : '');
    }
    return quote.dimensions || '';
  };

  // Filter and sort quotes
  const filteredQuotes = quotes.filter(quote => {
    const name = getQuoteName(quote);
    const summary = getQuoteSummary(quote);
    const searchTermLower = searchTerm.toLowerCase();

    return (
      name?.toLowerCase().includes(searchTermLower) ||
      quote.clientName?.toLowerCase().includes(searchTermLower) ||
      quote.customerName?.toLowerCase().includes(searchTermLower) ||
      summary?.toLowerCase().includes(searchTermLower) ||
      quote.id?.toLowerCase().includes(searchTermLower)
    );
  });

  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    if (sortBy === 'date') {
      return sortOrder === 'asc'
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt);
    } else if (sortBy === 'price') {
      const priceA = a.totalPrice || a.price || 0;
      const priceB = b.totalPrice || b.price || 0;
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
    } else if (sortBy === 'status') {
      return sortOrder === 'asc'
        ? (a.status || '').localeCompare(b.status || '')
        : (b.status || '').localeCompare(a.status || '');
    }
    return 0;
  });

  // Upload modal functions
  const openUploadModal = (quote) => {
    setUploadTargetQuote(quote);
    setShowUploadModal(true);
    setUploadStatusMessage('');
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadTargetQuote(null);
  };

  const handleUploadComplete = (success) => {
    if (success) {
      console.log(`Files uploaded successfully for quote ${uploadTargetQuote?.id}`);
      setUploadStatusMessage('Files uploaded successfully!');
      setTimeout(() => {
        closeUploadModal();
      }, 1500);
    } else {
      console.log(`File upload failed or had errors for quote ${uploadTargetQuote?.id}`);
      setUploadStatusMessage('Upload failed. Please check errors below.');
    }
  };

  // If a submission requested an upload, open the modal once quotes are available
  useEffect(() => {
    if (!pendingUploadQuoteId || isLoading) {
      return;
    }

    const target = quotes.find((quote) => quote.id === pendingUploadQuoteId);
    if (target) {
      openUploadModal(target);
      clearPendingUploadRequest();
    }
  }, [pendingUploadQuoteId, quotes, isLoading, clearPendingUploadRequest]);

  // Loading and error states
  if (isLoading) {
    return <div className="text-center py-8">Loading your quotes...</div>;
  }
  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }
  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-semibold mb-4">Login Required</h2>
        <p className="text-gray-600 mb-4">Please login to view your quotes.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-6">{pageTitle}</h1>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <input
              type="text"
              placeholder="Search by name, customer, details, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="date">Sort by Date</option>
              <option value="price">Sort by Price</option>
              <option value="status">Sort by Status</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              title={sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
            >
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200 flex justify-between items-center">
          <span>Showing {quotes.length} of {quotesTotal || quotes.length} quotes</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Type</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Quote / Job Name</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Customer</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Details</th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Price</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Date</th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-56">Actions</th> {/* Increased width for new button */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedQuotes.length > 0 ? (
                sortedQuotes.map((quote) => {
                  const quoteType = getQuoteType(quote);
                  const allowUpload = !['rejected', 'cancelled'].includes(quote.status);

                  return (
                    <tr key={quote.id} className="hover:bg-gray-50 align-top">
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(quoteType)}`}>
                          {quoteType}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-normal break-words">
                        <div className="text-sm font-medium text-gray-900">{getQuoteName(quote)}</div>
                        <div className="text-xs text-gray-500">ID: {quote.id?.substring(0, 8)}...</div>
                      </td>
                      <td className="px-3 py-4 whitespace-normal break-words text-sm text-gray-700">
                        {quote.companyName || quote.clientName || quote.customerName || quote.username || 'N/A'}
                      </td>
                      <td className="px-3 py-4 whitespace-normal break-words">
                        <div className="text-sm text-gray-900">{getQuoteSummary(quote)}</div>
                        {getQuoteDimensions(quote) && (
                          <div className="text-sm text-gray-500">
                            {getQuoteDimensions(quote)} {quote.items?.[0]?.quantity && `× ${quote.items[0].quantity}`}
                          </div>
                        )}
                        {((quote.numberOfDesigns > 1) || (quote.items?.[0]?.numberOfDesigns > 1)) && (
                          <div className="text-sm text-gray-500">
                            {(quote.numberOfDesigns || quote.items?.[0]?.numberOfDesigns)} designs
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          £{(quote.totalPrice || quote.price || 0).toFixed(2)}
                        </div>
                        {quote.status === 'adjusted' && quote.originalPrice != null && (
                          <div className="text-xs text-gray-500 line-through">
                            £{quote.originalPrice.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(quote.status)}`}>
                          {quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(quote.createdAt)}
                      </td>
                      {/* --- UPDATED Actions Cell with Edit Similar --- */}
                      <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex flex-wrap gap-1 justify-end">
                          <button
                            title="View Details"
                            className="text-indigo-600 hover:text-indigo-900 transition-colors px-1"
                            onClick={() => viewQuoteDetails(quote)}
                          >
                            View
                          </button>

                          {/* NEW: Edit Similar Button */}
                          <button
                            title="Create similar quote with these settings"
                            className="text-green-600 hover:text-green-900 transition-colors px-1"
                            onClick={() => handleEditSimilar(quote)}
                          >
                            Edit Similar
                          </button>

                          {allowUpload && (
                            <button
                              title="Upload Artwork/Files"
                              className="text-purple-600 hover:text-purple-900 transition-colors px-1"
                              onClick={() => openUploadModal(quote)}
                            >
                              Upload
                            </button>
                          )}

                          {quote.status === 'adjusted' && (
                            <button
                              title="Approve Adjusted Price"
                              className="text-green-600 hover:text-green-900 transition-colors px-1"
                              onClick={() => handleApproveAdjusted(quote.id)}
                            >
                              Approve
                            </button>
                          )}

                          <button
                            title="Delete Quote"
                            className="text-red-600 hover:text-red-900 transition-colors px-1"
                            onClick={() => handleDeleteQuote(quote.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                    {searchTerm
                      ? 'No quotes match your search criteria.'
                      : 'No quotes found. Create a quote using the Price Calculator.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {quotesHasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={loadMoreQuotes}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
          >
            {isLoading ? 'Loading...' : 'Load more quotes'}
          </button>
        </div>
      )}

      {/* Existing Quote Details Modal */}
      {selectedQuote && (
        <Modal isOpen={!!selectedQuote} onClose={closeQuoteDetails} title={`Quote Details: ${getQuoteName(selectedQuote)}`} size="4xl">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-lg mb-2">Customer Information</h3>
                <p><span className="font-medium">Client Name:</span> {selectedQuote.clientName || selectedQuote.customerName || selectedQuote.username || 'N/A'}</p>
                {selectedQuote.companyName && (
                  <p><span className="font-medium">Company Name:</span> {selectedQuote.companyName}</p>
                )}
                <p><span className="font-medium">ID:</span> {selectedQuote.id}</p>
                <p><span className="font-medium">Created:</span> {formatDate(selectedQuote.createdAt)}</p>
              </div>

              {/* Quote Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-lg mb-2">Quote Information</h3>
                <p><span className="font-medium">Type:</span> {getQuoteType(selectedQuote)}</p>
                <p><span className="font-medium">Items:</span> {selectedQuote.items?.length || 1}</p>
                <p><span className="font-medium">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadge(selectedQuote.status)}`}>
                    {selectedQuote.status.charAt(0).toUpperCase() + selectedQuote.status.slice(1)}
                  </span>
                </p>
              </div>
            </div>

            {/* Products List */}
            {selectedQuote.items && selectedQuote.items.length > 0 && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-lg mb-4">Products</h3>
                <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                  {selectedQuote.items.map((item, index) => {
                    const isBoardsJobType = item.jobType === 'Boards';

                    return (
                      <div key={item.id || index} className="py-3 first:pt-0 last:pb-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{item.jobName || `Product ${index + 1}`}</p>

                            {item.productType === 'small-format' || item.productType === 'small_format' ? (
                              <>
                                <p className="text-sm text-gray-600">Paper: {item.paperStockName || item.paperStock || 'N/A'}</p>
                                <p className="text-sm text-gray-600">Size: {item.dimensions || `${item.width}x${item.height}mm`}</p>
                                {item.isBooklet && <p className="text-sm text-gray-600">Pages: {item.pageCount}</p>}
                                <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                                {item.numberOfDesigns && item.numberOfDesigns > 1 && (
                                  <p className="text-sm text-gray-600">Designs: {item.numberOfDesigns}</p>
                                )}

                                {item.processes && item.processes.length > 0 && (
                                  <p className="text-sm text-gray-600">
                                    Finishing: {item.processes.map((process, idx) => {
                                      const isLaminate = process.toLowerCase().includes('laminate');
                                      const processId = isLaminate ?
                                        process.toLowerCase().includes('duplex') ? 'duplex-laminate' : 'laminate'
                                        : '';
                                      const finishingOpts = item.finishingOptions || item.finishing_options;
                                      const laminateType = isLaminate && finishingOpts &&
                                        finishingOpts[processId] &&
                                        finishingOpts[processId]['laminateType'] ?
                                        ` (${getLaminateTypeName(finishingOpts[processId]['laminateType'])})`
                                        : '';

                                      return (
                                        <span key={idx}>
                                          {idx > 0 ? ', ' : ''}
                                          {process}{laminateType}
                                        </span>
                                      );
                                    })}
                                  </p>
                                )}

                                {item.isBooklet && item.separateCover && item.coverProcesses &&
                                  item.coverProcesses.length > 0 && (
                                    <p className="text-sm text-gray-600">
                                      Cover Finishing: {item.coverProcesses.map((process, idx) => {
                                        const isLaminate = process.toLowerCase().includes('laminate');
                                        const processId = isLaminate ?
                                          process.toLowerCase().includes('duplex') ? 'duplex-laminate' : 'laminate'
                                          : '';
                                        const coverFinishingOpts = item.coverFinishingOptions || item.cover_finishing_options;
                                        const laminateType = isLaminate && coverFinishingOpts &&
                                          coverFinishingOpts[processId] &&
                                          coverFinishingOpts[processId]['laminateType'] ?
                                          ` (${getLaminateTypeName(coverFinishingOpts[processId]['laminateType'])})`
                                          : '';

                                        return (
                                          <span key={idx}>
                                            {idx > 0 ? ', ' : ''}
                                            {process}{laminateType}
                                          </span>
                                        );
                                      })}
                                    </p>
                                  )}
                              </>
                            ) : item.productType === 'large-format' ? (
                              <>
                                <p className="text-sm text-gray-600">
                                  Material: {
                                    isBoardsJobType ?
                                      (item.materials?.find(m => m.type === 'board')?.name || 'N/A') :
                                      (item.materialNames || item.material || 'N/A')
                                  }
                                </p>
                                <p className="text-sm text-gray-600">Size: {item.dimensions || `${item.width}x${item.height}mm`}</p>
                                <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                                {item.numberOfDesigns && item.numberOfDesigns > 1 && (
                                  <p className="text-sm text-gray-600">Designs: {item.numberOfDesigns}</p>
                                )}
                                {!isBoardsJobType && item.processes && item.processes.length > 0 && (
                                  <p className="text-sm text-gray-600">
                                    Finishing: {Array.isArray(item.processes) ? item.processes.join(', ') : item.processes}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-sm text-gray-600">Details not available.</p>
                            )}
                          </div>
                          <p className="font-medium whitespace-nowrap pl-2">£{item.price?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pricing Information */}
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-lg mb-2">Pricing Details</h3>
              <div className="flex justify-between items-center">
                <div>
                  {selectedQuote.status === 'adjusted' && selectedQuote.originalPrice != null && (
                    <div className="mb-1">
                      <span className="font-medium">Original Price: </span>
                      <span className="line-through">£{selectedQuote.originalPrice.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="text-xl font-bold">
                    <span className="font-medium">Total Price: </span>
                    £{(selectedQuote.totalPrice || selectedQuote.price || 0).toFixed(2)}
                  </div>
                </div>
                {selectedQuote.status === 'adjusted' && (
                  <button
                    onClick={() => handleApproveAdjusted(selectedQuote.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Accept New Price
                  </button>
                )}
              </div>
            </div>

            {/* Notes Section */}
            {(selectedQuote.notes || ((selectedQuote.status === 'rejected' || selectedQuote.status === 'adjusted') && selectedQuote.managerNotes)) && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                {selectedQuote.notes && (
                  <div className="mb-4 last:mb-0">
                    <h3 className="font-medium text-lg mb-2">Your Notes</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedQuote.notes}</p>
                  </div>
                )}
                {(selectedQuote.status === 'rejected' || selectedQuote.status === 'adjusted') && selectedQuote.managerNotes && (
                  <div>
                    <h3 className="font-medium text-lg mb-2">Manager Notes</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedQuote.managerNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Status-specific messages */}
            {selectedQuote.status === 'rejected' && (
              <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-lg">
                <p className="font-medium">This quote has been rejected.</p>
                <p>Please see the manager notes above for details or create a new quote.</p>
              </div>
            )}
            {selectedQuote.status === 'processing' && (
              <div className="mt-6 p-4 bg-purple-50 text-purple-700 rounded-lg">
                <p className="font-medium">This quote is being processed!</p>
                <p>Your order is in our print system and is being prepared for production.</p>
                {selectedQuote.printlogic_order_number && (
                  <p className="mt-2">Order Reference: {selectedQuote.printlogic_order_number}</p>
                )}
              </div>
            )}
          </div>
          {/* Footer with actions */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
            <button
              onClick={() => handleDeleteQuote(selectedQuote.id)}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete Quote
            </button>
            <button
              onClick={closeQuoteDetails}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* File Upload Modal */}
      {showUploadModal && uploadTargetQuote && (
        <Modal
          isOpen={showUploadModal}
          onClose={closeUploadModal}
          title={`Upload Files for Quote: ${getQuoteName(uploadTargetQuote)}`}
          size="2xl"
        >
          {uploadStatusMessage && (
            <div className={`p-3 mb-4 rounded text-center text-sm font-medium ${uploadStatusMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
              {uploadStatusMessage}
            </div>
          )}

          <FileUpload
            jobId={uploadTargetQuote.id}
            quoteName={getQuoteName(uploadTargetQuote)}
            onUploadComplete={handleUploadComplete}
          />

          {/* Add Printroom Ready Button */}
          <div className="mt-6">
            <PrintroomReadyButton
              jobId={uploadTargetQuote.id}
              orderNumber={uploadTargetQuote.printlogic_order_number}
              onTrigger={(result) => {
                // Handle successful printroom notification
                console.log('Printroom notification triggered:', result);
                // You could show a success message or update the quote status
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default QuotesList;
